import { Style } from '@/constants';
import { StyleProperties } from './style.type';

// Confirmed dead beyond the constructor's real branch and `reduceStyles`
// (verified by grep, 2026-08-17): `asArray`, `processInheritance`,
// `reduceProperties`, `getDependencies`, `overwriteStyle`,
// `remapStyleProperties`, `mapDeviceProperties`, `mapProperties`, `clean`,
// and `get` have zero callers anywhere in `src/` -- the constructor's
// inheritance-processing logic they'd support is entirely commented out
// below. Typed rather than deleted (not this pass's call to make), same
// dead-code precedent as `components/action-bar/ActionBar.tsx` elsewhere in
// this codebase.
export class Styles {
	articleStyleMap: Map<string, Style> = new Map();
	deviceStyleMap: Map<string, Style> = new Map();
	customStyleMap: Map<string, Style> = new Map();

	styles: Map<string, Style> = new Map();
	constructor(styles?: Array<Style>) {
		/*for (const style of styles) {
			const { id, type } = style;
			switch (type) {
				case 'article':
					this.articleStyleMap.set(`${id}`, style);
					break;
				case 'device':
					this.deviceStyleMap.set(`${id}`, style);
					break;
				default:
					this.customStyleMap.set(`${id}`, style);
			}
		}

		this.processInheritance();

		this.remapStyleProperties(this.articleStyleMap);
		this.remapStyleProperties(this.deviceStyleMap);
		this.remapStyleProperties(this.customStyleMap);

		this.styles = new Map([
			...this.articleStyleMap,
			...this.customStyleMap,
			...this.deviceStyleMap,
		]);*/

		if (styles && styles.length) {
			this.styles = styles.reduce(reduceStyles, new Map());
		}
	}

	asArray = () => {
		const styles = [];
		for (const [, style] of this.styles.entries()) {
			styles.push(style);
		}

		return styles;
	};

	// Create the inheritance tree for all the styles
	processInheritance = () => {
		// Process article styles inheritance
		for (const [id, style] of this.articleStyleMap.entries()) {
			const { parent } = style;
			const properties = (style.properties ?? {}) as StyleProperties;
			if (!properties.buttons) {
				properties.buttons = [];
			}

			let overwriteProperties = { ...properties };
			const dependencies = this.getDependencies(parent, [
				clone(overwriteProperties),
			]);

			for (const dependency of dependencies) {
				overwriteProperties = this.overwriteStyle(
					dependency,
					clone(overwriteProperties),
				);
			}
			style.properties = overwriteProperties;
			this.articleStyleMap.set(id, style);
		}

		// Process custom styles inheritance
		for (const [id, style] of this.customStyleMap.entries()) {
			const { parent } = style;
			const properties = (style.properties ?? {}) as StyleProperties;
			if (!properties.buttons) {
				properties.buttons = [];
			}

			let overwriteProperties = { ...properties };
			const dependencies = this.getDependencies(parent, [
				clone(overwriteProperties),
			]);

			for (const dependency of dependencies) {
				overwriteProperties = this.overwriteStyle(
					dependency,
					clone(overwriteProperties),
				);
			}
			style.properties = overwriteProperties;
			this.customStyleMap.set(id, style);
		}
	};

	static reduceProperties = (acc: StyleProperties, properties: StyleProperties) => {
		for (const property in properties) {
			// The property do not exist so we just add it
			if (!acc[property]) {
				acc[property] = properties[property];
				continue;
			}

			// The property is an array so we just concatenate
			if (Array.isArray(properties[property])) {
				acc[property] = [
					...(acc[property] as Array<unknown>),
					...(properties[property] as Array<unknown>),
				];
				continue;
			}

			acc[property] = {
				...(acc[property] as StyleProperties),
				...(properties[property] as StyleProperties),
			};
		}
		return acc;
	};

	getDependencies(parent: string | null | undefined, dependencies: Array<StyleProperties>): Array<StyleProperties> {
		const style = parent ? this.articleStyleMap.get(parent) : null;
		if (style) {
			const { parent, properties } = style;
			if (properties) {
				dependencies.unshift({ ...(properties as StyleProperties) });
			}
			return this.getDependencies(parent, dependencies);
		}

		return dependencies;
	}

	overwriteStyle(style: StyleProperties, overwrite: StyleProperties = {}): StyleProperties {
		if (!overwrite) {
			return clone(style);
		}
		overwrite = { ...overwrite };
		style = clone(style);
		for (const property in overwrite) {
			// The property do not exist so we just add it
			if (!style[property]) {
				style[property] = overwrite[property];
				continue;
			}

			// The property is an array so we just concatenate
			if (Array.isArray(overwrite[property])) {
				if (!style[property]) {
					style[property] = [];
				}
				style[property] = [
					...(style[property] as Array<unknown>),
					...(overwrite[property] as Array<unknown>),
				];
				continue;
			}

			style[property] = {
				...(style[property] as StyleProperties),
				...(overwrite[property] as StyleProperties),
			};
		}

		if (style['styleblocks']) {
			delete style['styleblocks'];
		}

		return style;
	}

	remapStyleProperties = (styleMap: Map<string, Style>) => {
		for (const [id, style] of styleMap.entries()) {
			style.properties = this.mapDeviceProperties(
				clone(style.properties as StyleProperties),
			);
			styleMap.set(id, style);
		}
	};

	mapDeviceProperties = (style: StyleProperties): StyleProperties => {
		for (const property in style) {
			if (Array.isArray(style[property])) {
				style[property] = (style[property] as Array<unknown>).map((i) =>
					Styles.mapProperties(i as StyleProperties),
				);
				continue;
			}
			style[property] = Styles.mapProperties(
				style[property] as StyleProperties,
			);
		}
		return style;
	};

	static mapProperties(rawProperties: StyleProperties) {
		if (!rawProperties) {
			return {};
		}
		const properties = clone(rawProperties) as Record<
			string,
			string | number | undefined
		>;
		return Styles.clean({
			case: properties['case'],
			componentBottomMargin: properties['component_margin_bottom'],
			fontFamily: properties['font_family'],
			tabTextColor: properties['tab_text_color']
				? `#${properties['tab_text_color']}`
				: null,
			tabPaddingVertical: properties['tab_padding_vertical']
				? parseInt(`${properties['tab_padding_vertical']}`)
				: null,
			tabPaddingHorizontal: properties['tab_padding_horizontal']
				? parseInt(`${properties['tab_padding_horizontal']}`)
				: null,
			tabSelectedTextColor: properties['tab_selected_text_color']
				? `#${properties['tab_selected_text_color']}`
				: null,
			tabSelectedHighlightColor: properties[
				'tab_selected_highlight_color'
			]
				? `#${properties['tab_selected_highlight_color']}`
				: null,
			tabSelectedBackgroundColor: properties[
				'tab_selected_background_color'
			]
				? `#${properties['tab_selected_background_color']}`
				: null,
			tabBackgroundColor: properties['tab_background_color']
				? `#${(properties['tab_background_color'] as string).replace('#', '')}`
				: null,
			tabContentTopMargin: properties['tab_content_top_margin'],
			fontSize: properties['font_size'],
			color: properties['color']
				? `#${(properties['color'] as string).replace('#', '')}`
				: null,
			fontWeight: properties['font_weight'],
			fontStyle: properties['font_style'],
			fontFamilyBold: properties['font_family_bold'],
			fontFamilyItalic: properties['font_family_italic'],
			fontFamilyBoldItalic: properties['font_family_bold_italic'],
			backgroundColor: properties['background_color']
				? `#${(properties['background_color'] as string).replace('#', '')}`
				: null,
			backgroundColorEnabled: properties['background_color_enabled'],
			textDecoration: properties['text_decoration'],
			lineHeight: properties['line_height'],
			textTransform: properties['text_transform'],
			paragraphSpacing: properties['paragraph_spacing'],
			marginLeft: properties['margin_left'],
			marginRight: properties['margin_right'],
			marginBottom: properties['margin_bottom'],
			marginTop: properties['margin_top'],
			textAlign: properties['text_align'],
			letterSpacing: properties['letter_spacing'],
			css: properties['css'],
			textIndent: properties['text_indent'],
			contentAlign: properties['content_align'],
			borderType: properties['border_type'],
			borderEdge: properties['border_edge'],
			border_radius: properties['borderRadius'],
			border_radius_position: properties['borderRadiusPosition'],
			borderWidth: properties['border_width'],
			borderColor: properties['border_color']
				? `#${(properties['border_color'] as string).replace('#', '')}`
				: null,
			dropcapFontFamily: properties['dropcap_font_family'],
			dropcapBackgroundColor: properties['dropcap_background_color']
				? `#${(properties['dropcap_background_color'] as string).replace('#', '')}`
				: null,
			dropcapFontSize: properties['dropcap_font_size'],
			dropcapLineHeight: properties['dropcap_line_height'],
			dropcapMarginTop: properties['dropcap_margin_top'],
			dropcapMarginBottom: properties['dropcap_margin_bottom'],
			dropcapMarginLeft: properties['dropcap_margin_left'],
			dropcapMarginRight: properties['dropcap_margin_right'],
			dropcapPaddingTop: properties['dropcap_padding_top'],
			dropcapPaddingBottom: properties['dropcap_padding_bottom'],
			dropcapPaddingLeft: properties['dropcap_padding_left'],
			dropcapPaddingRight: properties['dropcap_padding_right'],
			dropcapColor: properties['dropcap_color']
				? `#${(properties['dropcap_color'] as string).replace('#', '')}`
				: null,
			textDisplay: properties['text_display'],
			paddingTop: properties['padding_top'],
			paddingBottom: properties['padding_bottom'],
			paddingLeft: properties['padding_left'],
			paddingRight: properties['padding_right'],
			horizontalPadding: properties['horizontal_padding'],
			bodyColor: properties['body_color']
				? `#${(properties['body_color'] as string).replace('#', '')}`
				: null,
			spacingUnit: properties['spacing_unit'],
			listStyle: properties['list_style'],
			style: properties['style'],
			thickness: properties['thickness'],
			margin: properties['margin'],
			width: properties['width'],
			captionPosition:
				properties['captionposition'] || properties['caption_position'],
			text: properties['text'],
			align: properties['align'],
			size: properties['size'],
			name: properties['name'],
			id: properties['id'],
			vSmallHeight: properties['vsmall_height'],
			smallHeight: properties['small_height'],
			mediumHeight: properties['medium_height'],
			largeHeight: properties['large_height'],
			vLargeHeight: properties['vlarge_height'],
		});
	}

	static clean(obj: StyleProperties) {
		for (const propName in obj) {
			if (obj[propName] === null || obj[propName] === undefined) {
				delete obj[propName];
			}
		}
		return obj;
	}

	get(id: string) {
		if (id) {
			return this.styles.get(id);
		}
		return this.styles;
	}
}

function clone<T>(obj: T): T {
	if (obj == null || typeof obj != 'object') return obj;

	const ctor = (obj as object).constructor as new () => Record<
		string,
		unknown
	>;
	const temp = new ctor();
	for (const key in obj) {
		temp[key] = clone((obj as Record<string, unknown>)[key]);
	}

	return temp as T;
}

export function reduceStyles(acc: Map<string, Style>, style: Style): Map<string, Style> {
	acc.set(`${style.id}`, style);
	return acc;
}